// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AppDialog from "@/components/common/AppDialog";
import { FormCalendar, FormInput, FormSelect, FormTextarea } from "@/components/forms/FormControls";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { PHASE_LABELS } from "@/utils/deals.utils";
import { REQUEST_TYPE_OPTIONS, ROLE_STYLES } from "@/constant/deals.constant";
import { taskSchema } from "@/schemas/deals";

const AUDIENCE_OPTIONS = [
  { value: "internal", label: "My team only" },
  { value: "externals", label: "Team + lender, attorney, other agent" },
  { value: "client", label: "Everyone, including the client" },
];

const RELEASE_OPTIONS = [
  { value: "auto", label: "As soon as it is added" },
  { value: "after_confirmed", label: "Once it is confirmed to have happened" },
  { value: "on_approval", label: "Only when I send it" },
];

const DUE_DATE_SOURCE_OPTIONS = [
  { value: "manual", label: "Specific date" },
  { value: "relative_to_closing", label: "Days before closing" },
];

export default function TaskDialog({ open, onClose, onSubmit, defaultValues, phases, submitting = false, onDelete = null, onToggleNeeded = null }) {
  // A contingency is just a task aimed at the slot the seller's under-contract
  // checklist reserves for it, so it reuses this dialog — only the wording and
  // the locked phase change.
  const isContingency = Boolean(defaultValues?.after_task_id);
  const isEdit = Boolean(defaultValues?.id);
  const waived = Boolean(defaultValues?.waived_at);
  // Delete asks twice in place — a browser confirm() would block the page.
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => { if (!open) setConfirmDelete(false); }, [open]);
  const availablePhases = useMemo(
    () => Array.isArray(phases) && phases.length
      ? phases
      : ["pre_listing", "listing", "under_contract"],
    [phases],
  );
  const initialValues = useMemo(() => ({
    title: "",
    assigned_role: "agent",
    phase: availablePhases[0],
    due_date: "",
    due_date_source: "manual",
    due_date_offset_days: 3,
    description: "",
    audience: "client",
    client_release: "auto",
    request_type: "none",
    ...defaultValues,
  }), [availablePhases, defaultValues]);
  const form = useForm({
    resolver: zodResolver(taskSchema),
    defaultValues: initialValues,
  });
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) form.reset({ ...initialValues });
    wasOpen.current = open;
  }, [open, form, initialValues]);
  const dueDateSource = form.watch("due_date_source") || "manual";
  // A request only makes sense on the client's own task, and only if the
  // client can see it — otherwise there is nobody to email.
  const canAskClient = form.watch("assigned_role") === "client" && form.watch("audience") === "client";
  useEffect(() => {
    const currentOffset = form.getValues("due_date_offset_days");
    if (dueDateSource === "relative_to_closing" && (currentOffset === "" || currentOffset == null)) {
      form.setValue("due_date_offset_days", 3, { shouldValidate: false });
    }
  }, [dueDateSource, form]);

  return (
    <AppDialog open={open} onOpenChange={(next) => { if (!next) onClose(); }} title={isEdit ? "Edit Task" : isContingency ? "Add Contingency" : "Add Task"} eyebrow={isEdit ? "Edit" : isContingency ? "Contract" : "New"} contentClassName="sm:max-w-lg" testId="deal-task-dialog">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" data-testid="task-modal" noValidate>
          <FormInput name="title" label={isContingency ? "Contingency" : "Task Name"} placeholder={isContingency ? "e.g., Buyer's home sale contingency is released" : "e.g., Arrange inspection report review"} data-testid="task-title" required />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormSelect name="phase" label="Task Type" options={availablePhases.map((phase) => ({ value: phase, label: PHASE_LABELS[phase] }))} data-testid="task-phase" required disabled={isContingency} />
            <FormSelect name="due_date_source" label="Due Date Rule" options={DUE_DATE_SOURCE_OPTIONS} data-testid="task-due-date-source" required />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {dueDateSource === "relative_to_closing" ? (
              <FormInput name="due_date_offset_days" label="Days Before Closing" type="number" min={0} max={365} step={1} data-testid="task-due-date-offset" required />
            ) : (
              <FormCalendar name="due_date" label="Due Date" data-testid="task-due-date" />
            )}
            <FormSelect name="assigned_role" label="Assignee" options={Object.keys(ROLE_STYLES).map((role) => ({ value: role, label: ROLE_STYLES[role].label }))} data-testid="task-role" required />
          </div>
          {canAskClient && (
            <FormSelect
              name="request_type"
              label="Ask the client to…"
              description="Stoop emails the client and sends reminders until it's done."
              options={REQUEST_TYPE_OPTIONS}
              data-testid="task-request-type"
            />
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormSelect name="audience" label="Who should see this?" options={AUDIENCE_OPTIONS} data-testid="task-audience" required />
          </div>
          {form.watch("audience") === "client" && (
            <FormSelect name="client_release" label="When should the client see this?" options={RELEASE_OPTIONS} data-testid="task-client-release" required />
          )}
          <FormTextarea name="description" label="Description" rows={4} placeholder="Enter task details, notes, or specific requirements here…" className="resize-none" data-testid="task-description" />
          <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-end">
            {isEdit && (onToggleNeeded || onDelete) && (
              <div className="flex flex-wrap gap-2 sm:mr-auto">
                {onToggleNeeded && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => onToggleNeeded(defaultValues, !waived)} disabled={submitting} data-testid="task-toggle-needed">
                    {waived ? "Needed after all" : "Not needed"}
                  </Button>
                )}
                {onDelete && (
                  <Button type="button" variant="ghost" size="sm" className="text-dash-rust hover:text-dash-rust" onClick={() => (confirmDelete ? onDelete(defaultValues) : setConfirmDelete(true))} disabled={submitting} data-testid="task-delete">
                    {confirmDelete ? "Click again to delete" : "Delete"}
                  </Button>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 sm:flex">
              <Button type="button" variant="dashboardOutline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button type="submit" variant="dashboardPrimary" data-testid="task-submit" loading={submitting}>{isEdit ? "Save changes" : "Add Task"}</Button>
            </div>
          </div>
        </form>
      </Form>
    </AppDialog>
  );
}
